/**
 * TrialPaywallModal
 *
 * Notion-clean 3-tier upgrade modal shown when trial limits are hit
 * or premium features are accessed. Middle tier (Pro/Agency) is
 * visually dominant with a "Most popular" badge.
 *
 * GHL-killer: transformation copy, zero feature-jungle, 1 clear action.
 */

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, Sparkles, Zap } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export type TrialPaywallReason =
  | "limit.runs"
  | "limit.active"
  | "feature.reseller"
  | "feature.workspace"
  | "feature.premium"
  | "manual";

interface TrialPaywallModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reason?: TrialPaywallReason;
  runsUsed?: number;
  runsLimit?: number;
}

interface TierConfig {
  id: "core" | "pro" | "teams";
  name: string;
  priceEur: number;
  pitch: string;
  features: string[];
  stackSlug: string;
  popular?: boolean;
}

const TIERS: TierConfig[] = [
  {
    id: "core",
    name: "Starter",
    priceEur: 99,
    pitch: "Voor solo-makers die direct waarde willen leveren.",
    features: [
      "10 AI-runs per maand",
      "1 workspace",
      "Alle Suites & Flows",
      "Bundel-export (MD, JSON)",
    ],
    stackSlug: "revenue-stack",
  },
  {
    id: "pro",
    name: "Agency",
    priceEur: 249,
    pitch: "Verdien recurring revenue met jouw eigen AI-platform.",
    features: [
      "50 AI-runs per maand",
      "3 actieve runs tegelijk",
      "Reseller-mode (eigen prijzen)",
      "White-label customer portal",
      "Premium templates & library",
    ],
    stackSlug: "operator-stack",
    popular: true,
  },
  {
    id: "teams",
    name: "Scale",
    priceEur: 499,
    pitch: "Voor teams die meerdere klanten autonoom bedienen.",
    features: [
      "200 AI-runs per maand",
      "10 actieve runs tegelijk",
      "10 team-seats",
      "Managed services",
      "Priority support",
    ],
    stackSlug: "growth-stack",
  },
];

const REASON_COPY: Record<TrialPaywallReason, { title: string; description: string }> = {
  "limit.runs": {
    title: "Je trial is op — tijd om te verdienen",
    description:
      "Je hebt je 3 gratis runs gebruikt. Upgrade nu en verdien binnen 24 uur geld met jouw AI-platform.",
  },
  "limit.active": {
    title: "Eén run tegelijk in trial",
    description:
      "Upgrade naar Agency en draai meerdere AI-flows parallel — voor jezelf én voor klanten.",
  },
  "feature.reseller": {
    title: "Reseller-mode is een Agency-feature",
    description:
      "Verkoop jouw AI-platform door aan klanten met je eigen prijzen, branding en marges.",
  },
  "feature.workspace": {
    title: "Meer workspaces nodig?",
    description:
      "Trial geeft 1 workspace. Upgrade naar Agency voor onbeperkte client-workspaces.",
  },
  "feature.premium": {
    title: "Premium module — alleen voor betaalde plannen",
    description:
      "Ontgrendel alle templates, premium flows en de volledige library.",
  },
  manual: {
    title: "Schaal jouw AI-platform",
    description:
      "Kies een plan dat past bij hoeveel klanten jij wilt bedienen.",
  },
};

export function TrialPaywallModal({
  open,
  onOpenChange,
  reason = "manual",
  runsUsed,
  runsLimit,
}: TrialPaywallModalProps) {
  const [pendingTier, setPendingTier] = useState<string | null>(null);
  const copy = REASON_COPY[reason];

  const handleUpgrade = async (tier: TierConfig) => {
    setPendingTier(tier.id);
    try {
      const { data, error } = await supabase.functions.invoke("checkout-stack", {
        body: { stack_slug: tier.stackSlug },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
        onOpenChange(false);
      } else {
        throw new Error("Geen checkout URL ontvangen");
      }
    } catch (err) {
      console.error("[TrialPaywallModal] checkout error", err);
      toast.error("Kon checkout niet starten", {
        description: err instanceof Error ? err.message : "Probeer opnieuw",
      });
    } finally {
      setPendingTier(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="space-y-3 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <Badge variant="secondary" className="font-normal">
              Trial → Paid
            </Badge>
          </div>
          <DialogTitle className="text-2xl sm:text-3xl tracking-tight">
            {copy.title}
          </DialogTitle>
          <DialogDescription className="text-base">
            {copy.description}
            {typeof runsUsed === "number" && typeof runsLimit === "number" && (
              <span className="block mt-1 text-sm text-muted-foreground">
                Trial gebruikt: <span className="font-medium text-foreground">{runsUsed}/{runsLimit} runs</span>
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 md:grid-cols-3 mt-6">
          {TIERS.map((tier) => {
            const isPopular = tier.popular;
            const isPending = pendingTier === tier.id;

            return (
              <div
                key={tier.id}
                className={cn(
                  "relative rounded-xl border p-6 flex flex-col gap-4 transition-all",
                  isPopular
                    ? "border-primary bg-primary/5 shadow-lg md:scale-105 md:-my-2"
                    : "border-border bg-card hover:border-muted-foreground/30",
                )}
              >
                {isPopular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 text-xs font-medium shadow-sm">
                    Most popular
                  </Badge>
                )}

                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">{tier.name}</h3>
                  <p className="text-sm text-muted-foreground min-h-[40px]">
                    {tier.pitch}
                  </p>
                </div>

                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-bold tracking-tight">
                    €{tier.priceEur}
                  </span>
                  <span className="text-sm text-muted-foreground">/maand</span>
                </div>

                <ul className="space-y-2 flex-1">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm">
                      <Check
                        className={cn(
                          "h-4 w-4 mt-0.5 shrink-0",
                          isPopular ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  size="lg"
                  variant={isPopular ? "default" : "outline"}
                  onClick={() => handleUpgrade(tier)}
                  disabled={pendingTier !== null}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Bezig…
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4 mr-2" />
                      Kies {tier.name}
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-center text-muted-foreground mt-6">
          Maandelijks opzegbaar · Geen setup-fees · Direct toegang na betaling
        </p>
      </DialogContent>
    </Dialog>
  );
}
